import React from 'react';
import styled from 'styled-components';
import { User } from '../../types';
import { useCheckout } from '@stripe/react-stripe-js/checkout';

const PriceLabel = styled.span`
  color: #1e1e1e;
  font-size: 16px;
  
  @media (min-width: 768px) {
    font-size: 20px;
  }
`;

const PriceValue = styled.div<{ isHighlight?: boolean }>`
  text-align: right;
  font-family: 'Lato';
  font-style: normal;
  font-weight: 700;
  font-size: 32px;
  line-height: 115%;
  color: #047C2B;

  font-size: ${props => props.isHighlight ? '24px' : '20px'};
  color: ${props => props.isHighlight ? '#047C2B' : '#000'};
  
  @media (min-width: 768px) {
    font-size: ${props => props.isHighlight ? '32px' : '20px'};
  }
`;

const PriceSubtext = styled.div`
  margin-top: 5px;
  display: block;
  font-style: normal;
  font-weight: 400;
  font-size: 12px;
  line-height: 115%;
  text-align: right;
  color: #7A7A7A;
`;

const DiscountText = styled.div`
  margin-top: 5px;
  display: block;
  font-style: italic;
  font-weight: 400;
  font-size: 12px;
  line-height: 115%;
  text-align: right;
  color: #7A7A7A;
  margin-top: 12px;
`;

const fixedAmountOffPromotions = {
  "STUDENT-ANNUAL": 69,
  "STUDENT-MONTHLY": 10,
};

export const AfterTrialPeriod = ({ user, showDiscountText = false }: { user: User, showDiscountText?: boolean }) => {
  const checkoutState = useCheckout();

  const formatPricingInfo = () => {
    let weeklyPrice = "";
    let annualPrice = "";
    let discountText = "";

    console.log("checkoutState type", checkoutState.type);
    if (checkoutState.type !== 'success') {
      return { weeklyPrice, annualPrice, discountText };
    }

    console.log("checkoutState", checkoutState.checkout);
    const subscriptionItem = checkoutState.checkout.lineItems.find((item: any) => {
      return !!item.unitAmount;
    });
    if (!subscriptionItem) {
      throw new Error('No subscription item found in line items');
    }

    const discount = subscriptionItem.discountAmounts?.[0];
    const percentOff = discount?.percentOff ?? 0;

    const discountName = discount?.displayName;
    const fixedAmountOff = discountName ? fixedAmountOffPromotions[discountName as keyof typeof fixedAmountOffPromotions] ?? 0 : 0;

    const interval = subscriptionItem.recurring?.interval || 'month';
    let amountInDollars = subscriptionItem.unitAmount.minorUnitsAmount / 100;

    if (percentOff > 0) {
      // Calculate the discounted price directly and round at the end to avoid precision errors
      amountInDollars = Math.floor(amountInDollars * (1 - percentOff / 100) * 100) / 100;
    } else if (fixedAmountOff > 0) {
      amountInDollars = amountInDollars - fixedAmountOff;
    }


    if (interval === 'year') {
      // Annual subscription
      const weeklyAmount = amountInDollars / 52;
      weeklyPrice = `$${weeklyAmount.toFixed(2)}/week`;
      annualPrice = `$${amountInDollars.toFixed(2)} billed annually`;

      if (percentOff > 0 && !discount?.recurring) {
        discountText = `${percentOff}% off your first year — renews at $${(subscriptionItem.unitAmount.minorUnitsAmount / 100).toFixed(2)}/yr after.`;
      } else if (fixedAmountOff > 0) {
        discountText = `$${fixedAmountOff} off your first year — renews at $${(subscriptionItem.unitAmount.minorUnitsAmount / 100).toFixed(2)}/yr after.`;
      }
    } else if (interval === 'month') {
      // Monthly subscription
      const weeklyAmount = (amountInDollars * 12) / 52;
      weeklyPrice = `$${weeklyAmount.toFixed(2)}/week`;
      annualPrice = `$${amountInDollars.toFixed(2)} billed monthly`;

      if (percentOff > 0 && (!discount?.recurring || (discount.recurring.type === "repeating" && discount.recurring.durationInMonths === 12))) {
        discountText = `${percentOff}% off your first 12 months — renews at $${(subscriptionItem.unitAmount.minorUnitsAmount / 100).toFixed(2)}/mo after.`;
      } else if (fixedAmountOff > 0) {
        discountText = `$${fixedAmountOff} off your first 12 months — renews at $${(subscriptionItem.unitAmount.minorUnitsAmount / 100).toFixed(2)}/mo after.`;
      }
    } else if (interval === 'week') {
      // Weekly subscription
      weeklyPrice = `$${amountInDollars.toFixed(2)}/week`;
      annualPrice = ``;
    }

    return { weeklyPrice, annualPrice, discountText };
  };

  const pricingInfo = formatPricingInfo();

  return (
    <>
      <PriceLabel>After Trial Period</PriceLabel>
      <div>
        <PriceValue>{pricingInfo.weeklyPrice}</PriceValue>
        <PriceSubtext>{pricingInfo.annualPrice}</PriceSubtext>
        {showDiscountText && pricingInfo.discountText && <DiscountText>{pricingInfo.discountText}</DiscountText>}
      </div>
    </>
  );
};
